import React, { useState } from 'react';
import {
    TableContainer, Paper, Table, TableRow, TableCell,
    TableHead, TableBody, Avatar, Checkbox, Typography, Box, Chip, Link,
    TableFooter,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { styled } from '@mui/system';


const StyledTableHead = styled(TableHead)(({ theme }) => ({
    '& .MuiTableCell-head': {
        fontWeight: '600',
        fontSize: '0.85rem',
        color: '#1b2124',
    },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    fontSize: '0.85rem',
    whiteSpace: 'nowrap',
    borderBottom: `1px solid #efefef`,
}));





const StatusChip = ({ status, color }) => {
    const theme = useTheme();
    const getStatusStyles = (status) => {
        switch (status) {
            case 'Generated':
            case 'Active':
            case 'Received':
            case 'Submitted':
            case 'Published':
                return {
                    bgColor: theme.palette.background.bggreenlight,
                    textColor: theme.palette.custom.greentextcolor,
                    dotColor: theme.palette.custom.greentextcolor,
                };
            case 'Transferred':
                return {
                    bgColor: theme.palette.background.bgskyblue,
                    textColor: theme.palette.custom.skybluetext,
                    dotColor: theme.palette.custom.skybluetext,
                };
            case 'Dropped-Out':
                return {
                    bgColor: theme.palette.background.bgyellowColor,
                    textColor: theme.palette.custom.yellowtext,
                    dotColor: theme.palette.custom.yellowtext,
                };
            case 'Pending':
                return {
                    bgColor: theme.palette.background.bgyellowColor,
                    textColor: theme.palette.custom.yellowtext,
                    dotColor: theme.palette.custom.yellowtext,
                };
            case 'Left':
                return {
                    bgColor: theme.palette.background.bggrey,
                    textColor: theme.palette.custom.greytextColor,
                    dotColor: theme.palette.custom.greytextColor,
                };
            case 'Paid':
                return {
                    bgColor: theme.palette.background.bggreenlight,
                    textColor: theme.palette.custom.greentextcolor,
                    dotColor: theme.palette.custom.greentextcolor,
                };
            case 'Open':
            case 'Not Submitted':
                return {
                    bgColor: theme.palette.background.bgredlight,
                    textColor: theme.palette.custom.redtextcolor,
                    dotColor: theme.palette.custom.redtextcolor,
                }
            case 'Close':
                return {
                    bgColor: theme.palette.background.bggreenlight,
                    textColor: theme.palette.custom.greentextcolor,
                    dotColor: theme.palette.custom.greentextcolor,
                }
            case 'Approved':
                return {
                    bgColor: theme.palette.background.bggreenlight,
                    textColor: theme.palette.custom.greentextcolor,
                    dotColor: theme.palette.custom.greentextcolor,
                };
            case 'open':
                return {
                    bgColor: theme.palette.background.bgredlight,
                    textColor: theme.palette.custom.redtextcolor,
                    dotColor: theme.palette.custom.redtextcolor,
                };

            case 'progress':
                return {
                    bgColor: theme.palette.background.bgskyblue,
                    textColor: theme.palette.custom.skybluetext,
                    dotColor: theme.palette.custom.skybluetext,
                };

            case 'resolved':
                return {
                    bgColor: theme.palette.background.bggreenlight,
                    textColor: theme.palette.custom.greentextcolor,
                    dotColor: theme.palette.custom.greentextcolor,
                };

            case 'closed':
                return {
                    bgColor: theme.palette.background.bggrey,
                    textColor: theme.palette.custom.greytextColor,
                    dotColor: theme.palette.custom.greytextColor,
                };

            case 'reopened':
            case 'Late':
                return {
                    bgColor: theme.palette.background.bgyellowColor,
                    textColor: theme.palette.custom.yellowtext,
                    dotColor: theme.palette.custom.yellowtext,
                };
            case 'Not Generated':
            case 'Expense':
            case 'Reject':
                return {
                    bgColor: theme.palette.background.bgredlight,
                    textColor: theme.palette.custom.redtextcolor,
                    dotColor: theme.palette.custom.redtextcolor,
                };
            case 'Partial':
                return {
                    bgColor: theme.palette.background.bgorangelight,
                    textColor: theme.palette.custom.redtextcolor,
                    dotColor: theme.palette.custom.redtextcolor,
                };
            default:
                return {
                    bgColor: theme.palette.background.bgredlight,
                    textColor: theme.palette.custom.redtextcolor,
                    dotColor: theme.palette.custom.redtextcolor,
                };
        }
    };

    const { bgColor, textColor, dotColor } = getStatusStyles(status);
    return (
        <Box
            sx={{
                padding: '4px 7px',
                textTransform: 'capitalize',
                fontWeight: 600,
                letterSpacing: '0.5px',
                borderRadius: '4px',
                backgroundColor: bgColor,
                color: textColor,
                display: 'inline-flex',
                alignItems: 'center',
                fontSize: '10px'
            }}
        >

            <Box
                sx={{
                    width: 6,
                    height: 6,
                    backgroundColor: dotColor,
                    borderRadius: '50%',
                    mr: 0.8
                }}
            />
            {status}
        </Box>
    );
};

const ClassBadge = ({ text, color }) => {
    return (
        <Box
            sx={{
                backgroundColor: color,
                color: '#000',
                px: 1.2,
                py: 0.3,
                borderRadius: '12px',
                fontSize: '0.75rem',
                fontWeight: 600,
                display: 'inline-block',
                minWidth: 24,
                textAlign: 'center'
            }}
        >
            {text}
        </Box>
    );
};

const CustomTable = ({ headers, rows, renderRow, register, className, footerRow, stickyHeader = false, tableHeight = '70vh', }) => {
    const theme = useTheme();
    const [selectedRows, setSelectedRows] = useState([]);

    const isSelectedAll = rows.length > 0 && selectedRows.length === rows.length;

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            setSelectedRows(rows.map((_, i) => i));
        } else {
            setSelectedRows([]);
        }
    };

    const handleRowClick = (index) => {
        const selectedIndex = selectedRows.indexOf(index);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = [...selectedRows, index];
        } else {
            newSelected = selectedRows.filter((i) => i !== index);
        }

        setSelectedRows(newSelected);
    };
    return (
        <TableContainer
            component={Paper}
            sx={{
                overflowX: 'auto',
                boxShadow: 'none',
                maxWidth: '100%',
                width: '100%',
                maxHeight: stickyHeader ? '70vh' : 'unset',
            }}
            className={className}
        >
            <Table size="small" stickyHeader={stickyHeader} className='voilet-custometable' sx={{
                overflowX: 'auto',
                scrollbarWidth: 'thin',
            }}
            >
                <StyledTableHead>
                    <TableRow>
                        {headers.map((header, index) => (
                            <StyledTableCell key={index}>
                                {header}
                            </StyledTableCell>
                        ))}
                    </TableRow>
                </StyledTableHead>
                <TableBody>
                    {rows.length > 0 && rows.map((row, i) => (
                        <TableRow key={i} hover>
                            {renderRow(row, i)}
                        </TableRow>
                    ))}
                </TableBody>
                {footerRow && (
                    <TableFooter
                        sx={{
                            "& .MuiTableCell-root": {
                                backgroundColor: theme.palette.background.Tableheadbg,
                                // color: '#1b2124',
                                fontWeight: 600,
                                fontSize: "0.85rem",
                                borderTop: `2px solid ${theme.palette.custom.borderColor}`,
                            },
                        }}
                    >
                        {footerRow}
                    </TableFooter>
                )}
            </Table>
        </TableContainer>
    );
};

export { CustomTable, StatusChip, ClassBadge };